import { useRef, useEffect } from 'react';

/**
 * Hook to track if a component is mounted.
 * Useful for preventing setState calls after unmount in async operations.
 *
 * @returns A ref that is true while the component is mounted, false after unmount
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const isMountedRef = useMountedRef();
 *
 *   useEffect(() => {
 *     fetchData().then((data) => {
 *       if (isMountedRef.current) {
 *         setData(data);
 *       }
 *     });
 *   }, []);
 * }
 * ```
 */
export function useMountedRef() {
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return isMountedRef;
}

export default useMountedRef;
